import { useMutation, useQuery } from "react-query";
import {
  doLogin,
  doResendOtp,
  doVerifyOtp,
  getCompanyExists,
  getControlPlaneCells,
  getControlPlaneCompanies,
  getUserAssociatedCompanies,
  getUserCompanyDetails,
  getUserDetails,
  getUserDetailsV2,
  getUserRoleAndPermission,
  createCompanyViaControlPlane,
  setDefaultProduct,
  switchCompany,
  switchCompanyOnControlPlane,
} from "../api/Esop";
import { LoginResponse } from "../types/Login";
import { queryClient } from "./client";
import { useAuthStore } from "../store";

export function uselogin() {
  return useMutation({
    mutationKey: "login",
    mutationFn: doLogin,
    onSuccess: (loginDto) => {
      if (loginDto && loginDto?.data?.reason !== "OTP Sent") {
        handleLogin(loginDto);
      }
      queryClient.invalidateQueries("userRoleAndPermission");
      queryClient.refetchQueries("userRoleAndPermission");
    },
  });
}

export function useUserDetails() {
  return useQuery({
    queryKey: "userDetails",
    queryFn: getUserDetails,
  });
}
export function useUserDetailsV2() {
  return useQuery({
    queryKey: "userDetailsV2",
    queryFn: getUserDetailsV2,
  });
}
export function useGetUserCompanyDetails() {
  return useQuery({
    queryKey: "companyDetails",
    queryFn: getUserCompanyDetails,
  });
}
export function useGetUserCompanies() {
  return useQuery({
    queryKey: "userCompanies",
    queryFn: getUserAssociatedCompanies,
    // onSuccess: (data) => {
    //   useAuthorizationStore.setState((store) => ({
    //     ...store,
    //     ...data.authorities[0],
    //   }));
    //   useAuthStore.setState((store) => ({
    //     ...store,
    //     user: data,
    //   }));
    // },
  });
}

export function useVerifyOtp() {
  return useMutation({
    mutationKey: "verification",
    mutationFn: doVerifyOtp,
    onSuccess: (verifiedLoginDto) => {
      if (verifiedLoginDto) handleLogin(verifiedLoginDto);
    },
    onError: () => {},
  });
}
export function useResendOtp() {
  return useMutation({
    mutationKey: "resend",
    mutationFn: doResendOtp,
  });
}

function handleLogin(loginDto: LoginResponse) {
  const accesstoken = loginDto.headers.accesstoken;
  if (accesstoken) {
    useAuthStore.setState((state) => ({ ...state, accessToken: accesstoken }));
  }
  queryClient.invalidateQueries("userDetails");
}

export function useGetCompanyExists(companyId: string) {
  return useQuery({
    queryKey: ["esopCompanyExists", companyId],
    queryFn: getCompanyExists,
  });
}

export function useSetDefaultProduct() {
  return useMutation({
    mutationKey: "setDefaultProduct",
    mutationFn: setDefaultProduct,
    onSuccess: () => {},
    onError: () => {},
  });
}
export function useSwitchCompany() {
  return useMutation({
    mutationKey: "switchCompany",
    mutationFn: switchCompany,
    onSuccess: (data) => {
      queryClient.resetQueries();
      queryClient.refetchQueries();
    },
    onError: () => {},
  });
}
export function useGetUserRoleAndPermission() {
  return useQuery({
    queryKey: "userRoleAndPermission",
    queryFn: getUserRoleAndPermission,
    onSuccess: (data) => {},
    onError: (data) => {},
    enabled: false,
  });
}

/**
 * Fetches the user's companies from the Control Plane.
 * Returns companies with cell/region info for display in the company switcher.
 */
export function useGetControlPlaneCompanies() {
  return useQuery({
    queryKey: "controlPlaneCompanies",
    queryFn: getControlPlaneCompanies,
    staleTime: 5 * 60 * 1000, // 5 min
    onError: () => {}, // Silently fail — data-plane list is still shown
  });
}

/**
 * Switches the active company on the Control Plane, updating the active_company
 * cookie so Kong routes future requests to the correct cell.
 */
export function useSwitchCompanyOnControlPlane() {
  return useMutation({
    mutationKey: "switchCompanyControlPlane",
    mutationFn: switchCompanyOnControlPlane,
  });
}

/**
 * Fetches all available cells from the Control Plane.
 */
export function useGetControlPlaneCells() {
  return useQuery({
    queryKey: "controlPlaneCells",
    queryFn: getControlPlaneCells,
    staleTime: 10 * 60 * 1000, // 10 min
  });
}

/**
 * Creates a company via the Control Plane.
 * If cellName provided, creates in that cell; otherwise creates in cell with least companies.
 */
export function useCreateCompanyViaControlPlane() {
  return useMutation({
    mutationKey: "createCompanyControlPlane",
    mutationFn: createCompanyViaControlPlane,
    onSuccess: () => {
      queryClient.invalidateQueries("controlPlaneCompanies");
    },
  });
}
